import {
  Link as ChakraLink,
  LinkProps as ChakraLinkProps
} from "@chakra-ui/react"
import path from "path-browserify"
import { forwardRef, useCallback } from "react"
import {
  LinkProps as RouterLinkProps,
  useHistory,
  useLocation,
  useRouteMatch
} from "react-router-dom"

// For navigating absolutely to the root of the url.
export type LinkTo = RouterLinkProps["to"]
// For navigating relative to the current url path.
// Since we use path.join, we can combine paths with special patterns, like "../",
// which would go back one level in the current pathname.
export type LinkRelativeTo = string
export type LinkHref = ChakraLinkProps["href"]

export type LinkToProps = { to?: LinkTo } | { relativeTo?: LinkRelativeTo }
export type LinkHrefProps = { href?: LinkHref }

export type LinkRouteProps = {
  to?: LinkTo
  relativeTo?: LinkRelativeTo
  href?: LinkHref
}

// Combined LinkProps type that supports either "to"/"relativeTo" or "href".
// Use generic type parameter T to specify which variant is being used,
// which props are required.
export type LinkProps<T extends "withTo" | "withHref" | "base" = "base"> =
  (Omit<ChakraLinkProps, "to" | "href"> &
    Omit<RouterLinkProps, "to" | "href">) &
    (T extends "withTo"
      ? Required<LinkToProps>
      : T extends "withHref"
      ? Required<LinkHrefProps>
      : LinkToProps | LinkHrefProps)

// Properly handle the "to" prop for navigation.
export function useHandleTo() {
  const location = useLocation()
  const history = useHistory()

  const handleTo = useCallback(
    (to: LinkTo) => {
      if (typeof to === "function") {
        history.push(to(location))
      } else {
        history.push(to)
      }
    },
    [location, history]
  )

  return handleTo
}

type LinkToObject = {
  pathname?: string
  search?: string
  hash?: string
  query?: Record<string, unknown>
}

const isAbsoluteHttpUrlStr = (urlStr: string) => urlStr.indexOf("http") === 0

export function pathsMatch(a: string, b: string): boolean {
  if (a === b) return true
  const normalize = (path: string) => (path.endsWith("/") ? path : `${path}/`)
  const aPathname = normalize(getUrlObjFromLinkTo(a)?.pathname || "")
  const bPathname = normalize(getUrlObjFromLinkTo(b)?.pathname || "")
  return aPathname === bPathname
}

/**
 * Resolves a `Link` `to` value to a `URL` (relative routes joined with
 * `window.location.origin`), matching legacy `@pathwright/ui` `getUrlObjFromRoute`.
 * Object routes without a string `pathname` return null (CoverPage behavior).
 * Hash on object routes is preserved (superset of legacy, which omitted it from `urlStr`).
 */
export function getUrlObjFromLinkTo(
  route: LinkTo | null | undefined
): URL | null {
  if (route == null) return null
  if (typeof route === "function") return null

  let urlStr = ""

  if (typeof route === "object") {
    const loc = route as LinkToObject
    const pathname = typeof loc.pathname === "string" ? loc.pathname : null
    if (!pathname) return null

    let searchStr = ""
    if (typeof loc.search === "string") {
      searchStr = loc.search
    } else {
      const sanitizedQuery = Object.entries(loc.query || {}).reduce<
        Record<string, string>
      >((query, [key, value]) => {
        if (value !== undefined && value !== null) {
          query[key] = String(value)
        }
        return query
      }, {})
      searchStr = new URLSearchParams(sanitizedQuery).toString()
    }
    if (searchStr && !searchStr.startsWith("?")) searchStr = `?${searchStr}`

    let hashPart = ""
    if (typeof loc.hash === "string" && loc.hash.length > 0) {
      hashPart = loc.hash.startsWith("#") ? loc.hash : `#${loc.hash}`
    }

    urlStr = pathname + searchStr + hashPart
  } else if (typeof route === "string") {
    urlStr = route
  } else {
    return null
  }

  try {
    if (isAbsoluteHttpUrlStr(urlStr)) {
      return new URL(urlStr)
    }
    if (typeof window === "undefined" || !window.location?.origin) {
      return null
    }
    return new URL(path.join(window.location.origin, urlStr))
  } catch {
    return null
  }
}

/** `pathname` + `search` + `hash` for a `to` value (legacy `getUrlFromRoute` + CoverPage `getUrlFromLinkTo`). */
export function getUrlFromLinkTo(
  route: LinkTo | null | undefined
): string | null {
  const url = getUrlObjFromLinkTo(route)
  if (!url) return null
  return `${url.pathname}${url.search}${url.hash}`
}

export function resolveToFromLinkProps(
  props: LinkProps | LinkToProps,
  url: string
): string | null {
  if ("to" in props && props.to) {
    return getUrlFromLinkTo(props.to)
  }
  if ("relativeTo" in props && props.relativeTo) {
    return path.join(url, props.relativeTo)
  }
  return null
}

export function resolveHrefFromLinkProps(
  props: LinkProps | LinkHrefProps,
  url: string
): string | null {
  const to = resolveToFromLinkProps(props, url)

  let href: string | null = "href" in props && props.href ? props.href : null
  // Provide an href by default (to support default behaviors, opening links
  // in a new tab using Meta + Click.
  if (to && !href) {
    const toUrl = getUrlFromLinkTo(to)

    const currentUrl = new URL(window.location.href)

    if (toUrl && (url.startsWith(toUrl) || toUrl.startsWith(url))) {
      // Derive the href prop based on whether the to prop is used in the current
      // URL's query params. If so, we update that query param with the to value.
      // Otherwise, we simply set the href to the to value.
      const matchingQueryParamEntry = Array.from(currentUrl.searchParams).find(
        ([, value]) => pathsMatch(value, url)
      )

      // If we've found a matching query param entry, we can derive the href using it.
      if (matchingQueryParamEntry) {
        const [queryParam] = matchingQueryParamEntry
        currentUrl.searchParams.set(queryParam, toUrl)
        href = currentUrl.href
      }
    }

    // Fallback to simply using the to value.
    if (!href && toUrl) {
      href = currentUrl.origin + toUrl || ""
    }
  }

  return href
}

const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ children, onClick, ...props }, ref) => {
    const { url } = useRouteMatch()
    const handleTo = useHandleTo()

    // Conditionally apply relativeTo or to based on the props.
    const to = resolveToFromLinkProps(props, url)

    // Provide an href by default (to support default behaviors, opening links
    // in a new tab using Meta + Click.
    const href = resolveHrefFromLinkProps(props, url)

    const target = "href" in props ? "_blank" : undefined
    const rel = "href" in props ? "noopener noreferrer" : undefined

    // Delete existing to prop to override.
    if ("to" in props) delete props.to
    // Delete existing relativeTo prop to not pass to link component.
    if ("relativeTo" in props) delete props.relativeTo
    // Delete existing href prop to override.
    if ("href" in props) delete props.href

    // Custom click handler to support more versatile route handling than what is provided by
    // react router's Link component which doesn't support an href prop (the href of the anchor
    // element is set to the to prop and cannot be overridden, which means we can't support user
    // clicking with modified events (Ctrl+click, Cmd+click, etc.).
    const handleClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        // Call any provided onClick handler first
        onClick?.(event)

        // Check for modified events (Ctrl+click, Cmd+click, etc.)
        if (
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.button !== 0
        ) {
          // Let the browser handle it naturally with the href
          return
        }

        // Check if we have a 'to' prop for React Router navigation
        if (to && !event.defaultPrevented) {
          event.preventDefault()
          handleTo(to)
        }

        // If no 'to' prop but we have href, let browser handle it naturally
      },
      [to, handleTo, onClick]
    )

    return (
      <ChakraLink
        ref={ref}
        className="Link"
        variant="underline"
        href={href || undefined}
        target={target}
        rel={rel}
        onClick={to ? handleClick : onClick}
        {...props}
      >
        {children}
      </ChakraLink>
    )
  }
)

Link.displayName = "Link"

export default Link
